import * as React from 'react';
import { PractiseRows, SkeletonRows, TextSkeleton, type CardProps, type FilterLink, type PractiseRow } from '@chess/api-insights/charts';
import { CardAside, CardEmpty, CardNote } from '@chess/api-dashboard/react';
import type { StageChange } from '@chess/api-model';
import type { LosingOpeningsResult } from './LosingOpeningsAggregator.ts';

// "Openings to work on" (2026-10-08; "Losing openings", the average score
// per opening, before): the openings the reader came out of behind most
// often, by family and colour, as rows in games, each a link to the library
// filtered to that opening, colour and the opening's "lost" bands
// (`stage-change`); each game's review starts where the book was left. The
// same shape as the Middlegame and Ending sections' second cards. Drawn
// from props only.

export type LosingOpeningsCardProps = CardProps<{ result: LosingOpeningsResult }, { link?: FilterLink }>;

export const TITLE = 'Openings to work on';
const ASIDE = 'games you came out behind';
const BEHIND: StageChange[] = ['opening-slightly-lost', 'opening-lost'];
const COLOUR = { white: 'as White', black: 'as Black' } as const;
const games = (n: number) => `${n} ${n === 1 ? 'game' : 'games'}`;

export const LosingOpeningsCard: React.FC<LosingOpeningsCardProps> = (props) => {
  if (props.loading)
    return (
      <>
        <CardAside>{ASIDE}</CardAside>
        <SkeletonRows labels={[undefined, undefined, undefined]} value="0" />
        <CardNote>
          <TextSkeleton sample="of 36 games you came out behind · a row opens its games" />
        </CardNote>
      </>
    );
  const { result, link } = props;
  const rows: PractiseRow[] = result.rows.map((r) => ({
    key: `${r.opening}|${r.colour}`,
    label: `${r.opening} · ${COLOUR[r.colour]}`,
    count: r.behind,
    // Deeper red the more of the row's games were clearly lost.
    colour: r.lost * 2 >= r.behind ? 'var(--viz-ending-slipped)' : r.lost ? 'var(--viz-ending-missed)' : 'var(--viz-ending-lost)',
    title: `${games(r.behind)} of ${r.scored} out of the opening behind (${r.lost} at −2.00 or worse) · opens them`,
    filter: { opening: r.opening, colour: r.colour, stageChange: BEHIND },
  }));
  if (!result.scored)
    return (
      <>
        <CardAside>{ASIDE}</CardAside>
        <CardEmpty>Scan games to score their openings.</CardEmpty>
      </>
    );
  if (!rows.length)
    return (
      <>
        <CardAside>{ASIDE}</CardAside>
        <CardEmpty>No opening left you behind in the last 30 days.</CardEmpty>
        <CardNote>{`of ${games(result.scored)} scored`}</CardNote>
      </>
    );
  return (
    <>
      <CardAside>{ASIDE}</CardAside>
      <PractiseRows rows={rows} link={link} />
      <CardNote>{`of ${games(result.behind)} you came out behind · a row opens its games; each review starts where you left the book`}</CardNote>
    </>
  );
};
